/**
 * Catmull-Rom spline interpolation for smoothing marker movement along a
 * sparse GPS route.
 *
 * WHY THIS EXISTS
 * ---------------
 * Trip geometry is recorded as GPS points that can be far apart. Moving a
 * marker with straight-line (linear) interpolation between two points makes
 * it travel in visible straight chords and change direction abruptly at every
 * point — a "kinked" look. A Catmull-Rom spline instead fits a smooth curve
 * that passes THROUGH each point, so the marker follows a gentle arc between
 * them with no kinks at the joints.
 *
 * It does NOT invent a more accurate path (it can't — the true road between
 * two GPS points is unknown). It only makes the motion between the real points
 * look natural instead of segmented.
 *
 * HOW IT WORKS
 * ------------
 * To interpolate a point between p1 and p2, the curve also looks at the point
 * before (p0) and the point after (p3). Those four surrounding points define
 * the tangents at p1 and p2, which is what bends the curve smoothly. `t` is the
 * fraction (0..1) of the way from p1 to p2.
 *
 * This is a pure function — no component state, no Leaflet dependency — so it
 * lives as a standalone helper rather than a mixin.
 */

/**
 * Interpolate a single coordinate value (e.g. latitude or longitude) using the
 * centripetal-uniform Catmull-Rom formula.
 *
 * @param {number} p0 value at the point before the segment
 * @param {number} p1 value at the start of the segment
 * @param {number} p2 value at the end of the segment
 * @param {number} p3 value at the point after the segment
 * @param {number} t  fraction 0..1 between p1 and p2
 * @returns {number} interpolated value
 */
function catmullRomValue(p0, p1, p2, p3, t) {
    const t2 = t * t
    const t3 = t2 * t
    // Standard uniform Catmull-Rom basis (tension 0.5).
    return 0.5 * (
        (2 * p1) +
        (-p0 + p2) * t +
        (2 * p0 - 5 * p1 + 4 * p2 - p3) * t2 +
        (-p0 + 3 * p1 - 3 * p2 + p3) * t3
    )
}

/**
 * Interpolate a lat/lng position along a route using a Catmull-Rom spline.
 *
 * @param {Array<{y:number,x:number}>} geometry route points ({y:lat, x:lng})
 * @param {number} index index of the segment's start point (p1)
 * @param {number} t     fraction 0..1 between geometry[index] and geometry[index+1]
 * @returns {{lat:number, lng:number}} smoothed position
 */
export function catmullRomPosition(geometry, index, t) {
    // Pick the four control points, clamping at the route ends so the first
    // and last segments still have a "before"/"after" point to reference.
    const i0 = Math.max(0, index - 1)
    const i1 = index
    const i2 = Math.min(geometry.length - 1, index + 1)
    const i3 = Math.min(geometry.length - 1, index + 2)

    const p0 = geometry[i0]
    const p1 = geometry[i1]
    const p2 = geometry[i2]
    const p3 = geometry[i3]

    return {
        lat: catmullRomValue(p0.y, p1.y, p2.y, p3.y, t),
        lng: catmullRomValue(p0.x, p1.x, p2.x, p3.x, t)
    }
}
